import { FC, useEffect, useState } from 'react';

import {
  getGiftCouponSettlementDetail,
  GiftCouponSettlementRole,
} from '@shared/api/couponSettlement/couponSettlement.controller';
import {
  GiftCouponSettlementDetailResponse,
  GiftCouponSettlementSummaryItem,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { bugoAgencyDeliveryPopulate } from '@shared/api/giftDelivery/giftDelivery.controller';
import { GiftDeliveryStatus } from '@shared/api/giftDelivery/giftDelivery.interface';
import { getGiftRebateSettlementById } from '@shared/api/giftRebateSettlement/giftRebateSettlement.controller';
import { GiftRebateSettlement } from '@shared/api/giftRebateSettlement/giftRebateSettlement.interface';
import { Loading } from '@shared/components/Loading';
import { LowerRole } from '@shared/types';

import { exportCouponSettlementDetailExcel } from '../../utils/couponSettlementExcel';
import { CouponHistoryDetailModal } from '../agencyGiftCouponSettlement/CouponHistoryDetailModal';
import { RebateHistoryDetailModal } from '../agencyGiftSettlement/RebateHistoryDetailModal';

// ─────────────────────────────────────────────
// UnifiedDetailModal
//
// 통합 '월별 합계' 표에서 정산서 상세를 **탭 이동 없이 바로** 띄운다.
// 목록이 들고 있는 문서는 상세 표시에 필요한 populate/라인이 빠져 있을 수 있어
// (배송형은 배송 메타, 쿠폰형은 라인 행) id로 다시 불러온 뒤 기존 상세 모달을
// 그대로 재사용한다 — 상세 화면은 각 탭과 100% 동일하다(읽기 전용).
// ─────────────────────────────────────────────

/** 열려는 상세 대상 (null이면 닫힘) */
export interface UnifiedDetailTarget {
  kind: 'delivery' | 'coupon';
  /** 정산서 _id */
  docId: string;
  /** 헤더 즉시 표시용 — 쿠폰형 목록이 이미 들고 있는 요약(없어도 동작) */
  couponSummary?: GiftCouponSettlementSummaryItem;
}

export interface UnifiedDetailModalProps {
  target: UnifiedDetailTarget | null;
  /** 조회 role — 상조는 BugoAgencyAdmin, 관리자/마리는 Admin·GiftAgent */
  role: LowerRole.Admin | LowerRole.BugoAgencyAdmin | LowerRole.GiftAgent;
  onClose: () => void;
  /** 조회 실패 안내 (없으면 조용히 닫는다) */
  onError?: (message: string) => void;
}

export const UnifiedDetailModal: FC<UnifiedDetailModalProps> = ({
  target,
  role,
  onClose,
  onError,
}) => {
  const [rebate, setRebate] = useState<GiftRebateSettlement | null>(null);
  const [coupon, setCoupon] = useState<GiftCouponSettlementDetailResponse | null>(null);

  useEffect(() => {
    setRebate(null);
    setCoupon(null);
    if (!target) return;

    // 늦게 도착한 응답이 다른 정산서의 모달을 덮어쓰지 않게 (닫힘/교체 시 폐기)
    let alive = true;
    const load = async () => {
      try {
        if (target.kind === 'delivery') {
          // 상세 모달이 배송 메타(구매자·고인명·지도사·배송일)를 쓰므로 라인 populate 필수
          const doc = await getGiftRebateSettlementById(
            target.docId,
            [{ path: 'lines.giftDelivery', populate: bugoAgencyDeliveryPopulate }],
            role,
          );
          // 취소(Canceled) 배송 라인은 합계에서 뺀다 — 목록 훅(useAgencyGiftSettlementData)과
          // 같은 방어 규칙이라야 표 금액과 모달 금액이 어긋나지 않는다
          if (alive)
            setRebate({
              ...doc,
              lines: doc.lines.filter((line) => {
                const d = line.giftDelivery;
                return !(
                  typeof d === 'object' &&
                  d?.deliveryStatus === GiftDeliveryStatus.Canceled
                );
              }),
            });
        } else {
          const detail = await getGiftCouponSettlementDetail(
            target.docId,
            role as GiftCouponSettlementRole,
          );
          if (alive) setCoupon(detail);
        }
      } catch (e: any) {
        if (!alive) return;
        onError?.(e?.response?.data?.message || '상세 조회에 실패했습니다.');
        onClose();
      }
    };
    load();
    return () => {
      alive = false;
    };
    // onClose/onError는 매 렌더 새로 만들어질 수 있어 의존성에서 제외 (대상 변경에만 반응)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.kind, target?.docId, role]);

  if (!target) return null;

  // 쿠폰형은 목록 요약(fallbackSummary)으로 헤더를 먼저 그릴 수 있어 즉시 모달을 띄운다.
  // 배송형은 문서 자체가 있어야 그려지므로 도착 전까지 스피너 오버레이.
  if (target.kind === 'coupon') {
    return (
      <CouponHistoryDetailModal
        detail={coupon}
        fallbackSummary={target.couponSummary}
        onClose={onClose}
        onExport={exportCouponSettlementDetailExcel}
        showAdditionalBadge
        showExcludedRows
      />
    );
  }

  if (!rebate) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="rounded-xl bg-white px-10 py-8 shadow-xl">
          <Loading />
        </div>
      </div>
    );
  }

  return <RebateHistoryDetailModal settlement={rebate} onClose={onClose} />;
};
